import React, { useState, useMemo } from 'react';
import {
  NodeStatusEvent,
  NodeEvent,
  NodeEventType,
  NodeDataType,
  ToolCallData,
} from '@/types/workflow-types';
import {
  Brain,
  Wrench,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  MessageSquare,
  Eye,
  Download,
} from 'lucide-react';
import { MarkdownRenderer } from '@/components/markdown';
import { JsonViewer } from '@/components/json-viewer';
import { CopyButton } from '@/components/ui/copy-button';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isNodeEvent, getEventMessage } from './helpers/panel-event-helpers';
import { EntryRow } from './entry-row';

type ContentType = 'text' | 'json' | 'markdown' | 'code';

type OpenChatFn = (event: NodeEvent) => void;

interface EventsTimelineItemProps {
  event: NodeStatusEvent;
  onOpenModal: (title: string, content: string, type: ContentType) => void;
  onOpenChat: OpenChatFn;
  isConnected?: boolean;
  isPaused?: boolean;
  isEntering?: boolean;
}

function formatModalContent(text: string): {
  content: string;
  type: ContentType;
} {
  const trimmed = text.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      return {
        content: JSON.stringify(JSON.parse(trimmed), null, 2),
        type: 'json',
      };
    } catch {
      // Not valid JSON — fall back to plain text.
    }
  }
  return { content: text, type: 'text' };
}

function getEventLabel(event: NodeStatusEvent): string {
  if (!isNodeEvent(event)) {
    return getEventMessage(event) || 'Event';
  }

  // The clarification flow (e.g. ``ask_user_clarification``) signals the wait
  // via AWAITING_USER_INPUT with a non-CHAT_REQUEST display_type, so key the
  // "Awaiting input" label off the event_type too.
  if (event.event_type === NodeEventType.AWAITING_USER_INPUT) {
    return 'Awaiting input';
  }

  switch (event.display_type) {
    case NodeDataType.THINKING:
      return 'Thinking';
    case NodeDataType.TOOL_CALL:
      return 'Calling tool';
    case NodeDataType.TOOL_RESULT:
      return 'Tool result';
    case NodeDataType.AGENT_MESSAGE:
    case NodeDataType.CHAT_AGENT_MESSAGE:
      return 'Agent response';
    case NodeDataType.NODE_RESULT:
      if (event.event_type === NodeEventType.EXECUTION_SUCCESSFUL) {
        return 'Complete';
      }
      if (event.event_type === NodeEventType.EXECUTION_FAILED) {
        return 'Failed';
      }
      return 'Result';
    case NodeDataType.ERROR:
      return 'Error';
    case NodeDataType.CHAT_REQUEST:
      return 'Awaiting input';
    case NodeDataType.CHAT_RESPONSE:
    case NodeDataType.CHAT_EVENT:
      return 'User response';
    default:
      break;
  }

  switch (event.event_type) {
    case NodeEventType.BEGIN:
      return 'Starting';
    case NodeEventType.END:
      return 'Ended';
    case NodeEventType.EXECUTION_SUCCESSFUL:
      return 'Complete';
    case NodeEventType.EXECUTION_FAILED:
      return 'Failed';
    case NodeEventType.RETRY_SCHEDULED:
      return 'Retrying';
    default:
      break;
  }

  return event.dynamic_message || 'Processing';
}

function formatTime(timestamp: string): string {
  return new Date(timestamp).toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getToolName(event: NodeEvent): string {
  const data = event.data;
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const record = data as Record<string, unknown>;
    return (record.tool_name as string) || 'Tool';
  }
  return 'Tool';
}

function getThinkingText(event: NodeEvent): string {
  if (typeof event.data === 'string') return event.data;
  return '';
}

function getAgentMessageText(event: NodeEvent): string {
  if (typeof event.data === 'string') return event.data;
  return '';
}

// The backend sends two result shapes: a node with a display_model emits its
// serialised display payload (readable text under `value`), anything else emits
// `{id, data, ...}`. A past-run load merges both, where `data` is the raw
// output and `value` the display string — so `value` wins when present.
// Never stringify the envelope: it carries s3 keys, presigned urls and the
// message history.
const RESULT_TEXT_KEYS = ['value', 'data'] as const;

function getNodeResultText(event: NodeEvent): string {
  if (typeof event.data === 'string') return event.data;
  if (
    event.data &&
    typeof event.data === 'object' &&
    !Array.isArray(event.data)
  ) {
    const envelope = event.data as Record<string, unknown>;
    const isEnvelope = RESULT_TEXT_KEYS.some((key) => key in envelope);
    for (const key of RESULT_TEXT_KEYS) {
      const readable = envelope[key];
      if (typeof readable === 'string') {
        // An empty display value must not shadow a populated `data`.
        if (readable) return readable;
        continue;
      }
      if (readable !== undefined && readable !== null) {
        return JSON.stringify(readable, null, 2);
      }
    }
    if (isEnvelope) return '';
  }
  return JSON.stringify(event.data, null, 2);
}

function hasVisibleContent(event: NodeStatusEvent): boolean {
  if (!isNodeEvent(event)) return false;

  const { display_type } = event;

  // Awaiting-input events render an "Open Chat" affordance regardless of
  // their display_type (CHAT_REQUEST or a bare UPDATE for tool-driven waits).
  if (event.event_type === NodeEventType.AWAITING_USER_INPUT) {
    return true;
  }

  if (
    display_type === NodeDataType.THINKING ||
    display_type === NodeDataType.TOOL_CALL ||
    display_type === NodeDataType.TOOL_RESULT ||
    display_type === NodeDataType.AGENT_MESSAGE ||
    display_type === NodeDataType.CHAT_AGENT_MESSAGE ||
    display_type === NodeDataType.ERROR ||
    display_type === NodeDataType.NODE_RESULT ||
    display_type === NodeDataType.CHAT_REQUEST ||
    display_type === NodeDataType.CHAT_RESPONSE ||
    display_type === NodeDataType.CHAT_EVENT
  ) {
    return true;
  }

  return false;
}

const CopyOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div className='absolute top-1.5 right-1.5'>
    <CopyButton text={text} size='small' ariaLabel='Copy content' />
  </div>
);

function downloadText(filename: string, text: string) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

const ContentActions: React.FC<{ text: string; downloadName: string }> = ({
  text,
  downloadName,
}) => (
  <div className='absolute top-1.5 right-1.5 flex items-center gap-0.5'>
    <CopyButton text={text} size='small' ariaLabel='Copy content' />
    <button
      type='button'
      onClick={() => downloadText(downloadName, text)}
      className='inline-flex items-center justify-center size-4 rounded-md text-muted-foreground hover:text-foreground transition-colors cursor-pointer'
      aria-label='Download'
    >
      <Download size={12} />
    </button>
  </div>
);

const IconBadge: React.FC<{
  icon: React.ReactNode;
  className?: string;
}> = ({ icon, className }) => (
  <div
    className={cn(
      'flex size-6 items-center justify-center rounded-md shrink-0',
      className,
    )}
  >
    {icon}
  </div>
);

const RESULT_PREVIEW_MAX_LENGTH = 4000;

const SCROLL_CONTENT_CLASS =
  'max-h-48 overflow-y-auto pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:bg-transparent [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-corner]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-muted-foreground/50 [scrollbar-width:thin] [scrollbar-color:rgba(128,128,128,0.3)_transparent]';

const ToolCallContent: React.FC<{ event: NodeEvent }> = ({ event }) => {
  const [showArgs, setShowArgs] = useState(false);
  const toolData = event.data as unknown as ToolCallData;
  const toolName = toolData?.tool_name || 'Tool';
  const argsString = toolData?.tool_arguments;

  // Only parse when the args panel is open, and memoize so re-renders of the
  // timeline don't re-parse potentially large tool payloads.
  const { parsedArgs, parseFailed } = useMemo(() => {
    if (!showArgs || !argsString) {
      return { parsedArgs: null as unknown, parseFailed: false };
    }
    try {
      return {
        parsedArgs: JSON.parse(argsString) as unknown,
        parseFailed: false,
      };
    } catch {
      return { parsedArgs: null as unknown, parseFailed: true };
    }
  }, [showArgs, argsString]);

  return (
    <div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
      <div className='flex items-center gap-2'>
        <IconBadge
          icon={<Wrench className='size-3.5 text-white' />}
          className='bg-linear-to-b from-slate-700 to-slate-900'
        />
        <span className='text-sm font-medium'>Using:</span>
        <code className='text-sm font-mono break-all'>{toolName}</code>
        {argsString && (
          <button
            type='button'
            aria-expanded={showArgs}
            onClick={() => setShowArgs((prev) => !prev)}
            className='ml-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer shrink-0'
          >
            {showArgs ? (
              <ChevronUp className='size-3' />
            ) : (
              <ChevronDown className='size-3' />
            )}
            {showArgs ? 'Hide args' : 'Show args'}
          </button>
        )}
      </div>
      {showArgs &&
        argsString &&
        (parseFailed ? (
          <pre className='p-2 bg-background rounded text-xs overflow-auto max-h-72 whitespace-pre-wrap break-all'>
            {argsString}
          </pre>
        ) : (
          <JsonViewer
            data={parsedArgs}
            maxHeight={150}
            className='text-xs'
            showCopyButton
            defaultView='readable'
          />
        ))}
    </div>
  );
};

const ChatRequestContent: React.FC<{
  event: NodeEvent;
  onOpenChat: OpenChatFn;
}> = ({ event, onOpenChat }) => {
  const data = event.data as { initial_message?: string } | undefined;

  const handleClick = () => {
    onOpenChat(event);
  };

  return (
    <div className='flex flex-col gap-2 p-3 bg-muted rounded-lg'>
      <div className='flex items-center gap-2'>
        <IconBadge
          icon={<MessageSquare className='size-3.5 text-white' />}
          className='bg-blue-500'
        />
        <span className='text-sm font-medium'>User input requested</span>
      </div>
      {data?.initial_message && (
        <p className='text-sm text-muted-foreground leading-relaxed'>
          {data.initial_message}
        </p>
      )}
      <Button
        variant='outline'
        size='sm'
        onClick={handleClick}
        className='w-fit gap-1.5'
      >
        <MessageSquare className='size-3.5' />
        Open Chat
      </Button>
    </div>
  );
};

const ChatMessageContent: React.FC<{
  event: NodeEvent;
  onOpenModal: (title: string, content: string, type: ContentType) => void;
}> = ({ event, onOpenModal }) => {
  const text =
    event.dynamic_message ||
    (typeof event.data === 'string'
      ? event.data
      : JSON.stringify(event.data, null, 2));
  if (!text) return null;
  const isLong = text.length > 300;

  return (
    <div className='relative flex flex-col gap-2 p-3 pr-8 bg-muted rounded-lg'>
      <CopyOverlay text={text} />
      <div className='text-sm leading-relaxed'>
        {isLong ? (
          <>
            <div className={SCROLL_CONTENT_CLASS}>
              <MarkdownRenderer content={text} />
            </div>
            <button
              onClick={() =>
                onOpenModal(
                  `${event.node_display_name} - Response`,
                  text,
                  'markdown',
                )
              }
              className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer w-fit mt-2'
            >
              <ArrowUpRight className='size-3' />
              Read more
            </button>
          </>
        ) : (
          <MarkdownRenderer content={text} />
        )}
      </div>
    </div>
  );
};

const EventContent: React.FC<{
  event: NodeEvent;
  onOpenModal: (title: string, content: string, type: ContentType) => void;
  onOpenChat: OpenChatFn;
  isConnected?: boolean;
  isPaused?: boolean;
}> = React.memo(
  ({
    event,
    onOpenModal,
    onOpenChat,
    isConnected = false,
    isPaused = false,
  }) => {
    if (event.event_type === NodeEventType.AWAITING_USER_INPUT) {
      return <ChatRequestContent event={event} onOpenChat={onOpenChat} />;
    }

    switch (event.display_type) {
      case NodeDataType.THINKING: {
        const text = getThinkingText(event);
        if (!text) return null;
        const isLong = text.length > 150;
        return (
          <div className='relative flex flex-col gap-2 p-3 pr-8 bg-muted rounded-lg'>
            <ContentActions
              text={text}
              downloadName={`cot-${(event.node_display_name || 'node')
                .replace(/[^a-z0-9]/gi, '-')
                .toLowerCase()}.txt`}
            />
            <div className='flex items-center gap-2'>
              <IconBadge
                icon={<Brain className='size-3.5 text-white' />}
                className='bg-purple-500'
              />
              <span className='text-sm font-medium'>
                {event.node_display_name}
              </span>
            </div>
            <p
              className={cn(
                'text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap',
                isLong && SCROLL_CONTENT_CLASS,
              )}
            >
              {text}
            </p>
            {isLong && (
              <button
                onClick={() =>
                  onOpenModal(
                    `${event.node_display_name} - Thinking`,
                    text,
                    'markdown',
                  )
                }
                className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer w-fit'
              >
                <ArrowUpRight className='size-3' />
                Read more
              </button>
            )}
          </div>
        );
      }

      case NodeDataType.TOOL_CALL:
        return <ToolCallContent event={event} />;

      case NodeDataType.TOOL_RESULT: {
        const toolName = getToolName(event);
        const resultText =
          typeof event.data === 'object' && event.data !== null
            ? ((event.data as Record<string, unknown>).tool_result as string) ||
              ''
            : '';
        return (
          <div className='flex items-center gap-2 p-3 bg-muted rounded-lg'>
            <IconBadge
              icon={<CheckCircle className='size-3.5 text-white' />}
              className='bg-green-600'
            />
            <span className='text-sm font-medium'>Result:</span>
            <code className='text-sm font-mono break-all'>{toolName}</code>
            {resultText && (
              <button
                onClick={() => {
                  const { content, type } = formatModalContent(resultText);
                  onOpenModal(`${toolName} - Result`, content, type);
                }}
                className='ml-auto flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer shrink-0'
              >
                <Eye className='size-3.5' />
                View full
              </button>
            )}
          </div>
        );
      }

      case NodeDataType.AGENT_MESSAGE:
      case NodeDataType.CHAT_AGENT_MESSAGE: {
        const text = getAgentMessageText(event);
        if (!text) return null;
        const isLong = text.length > 300;
        const couldStream =
          isConnected &&
          !isPaused &&
          event.event_type === NodeEventType.PROCESSING &&
          event.mode === 'textstream';
        return (
          <div className='relative flex flex-col gap-2 p-3 pr-8 bg-muted rounded-lg'>
            <ContentActions
              text={text}
              downloadName={`response-${(event.node_display_name || 'node')
                .replace(/[^a-z0-9]/gi, '-')
                .toLowerCase()}.txt`}
            />
            <div className='text-sm leading-relaxed'>
              {isLong ? (
                <>
                  <div className={SCROLL_CONTENT_CLASS}>
                    <MarkdownRenderer
                      content={text}
                      isStreaming={couldStream}
                    />
                  </div>
                  <button
                    onClick={() =>
                      onOpenModal(
                        `${event.node_display_name} - Response`,
                        text,
                        'markdown',
                      )
                    }
                    className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer w-fit mt-2'
                  >
                    <ArrowUpRight className='size-3' />
                    Read more
                  </button>
                </>
              ) : (
                <MarkdownRenderer content={text} isStreaming={couldStream} />
              )}
            </div>
          </div>
        );
      }

      case NodeDataType.ERROR: {
        const errorText =
          typeof event.data === 'string'
            ? event.data
            : JSON.stringify(event.data, null, 2);
        const isLong = errorText.length > 200;
        return (
          <div className='relative p-3 pr-8 bg-destructive/5 rounded-lg border border-destructive/20'>
            <CopyOverlay text={errorText} />
            <p
              className={cn(
                'text-sm text-destructive font-mono whitespace-pre-wrap break-all',
                isLong && SCROLL_CONTENT_CLASS,
              )}
            >
              {errorText}
            </p>
            {isLong && (
              <button
                onClick={() => {
                  const { content, type } = formatModalContent(errorText);
                  onOpenModal(
                    `${event.node_display_name} - Error`,
                    content,
                    type,
                  );
                }}
                className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer w-fit mt-2'
              >
                <ArrowUpRight className='size-3' />
                View full
              </button>
            )}
          </div>
        );
      }

      case NodeDataType.NODE_RESULT: {
        const resultText = getNodeResultText(event);
        // A file-only result has no text of its own; the files it produced are
        // listed in the Files tab, so an empty grey box would say nothing.
        if (!resultText) return null;
        const isLong = resultText.length > 200;
        // A result can run to hundreds of kilobytes; the scroll box only needs
        // enough to read, and the rest stays one click away behind "View full".
        const preview = resultText.slice(0, RESULT_PREVIEW_MAX_LENGTH);
        return (
          <div className='relative p-3 pr-8 bg-muted rounded-lg'>
            <CopyOverlay text={resultText} />
            <p
              className={cn(
                'text-sm text-muted-foreground font-mono whitespace-pre-wrap break-all',
                isLong && SCROLL_CONTENT_CLASS,
              )}
            >
              {preview}
            </p>
            {isLong && (
              <button
                onClick={() => {
                  const { content, type } = formatModalContent(resultText);
                  onOpenModal(
                    `${event.node_display_name} - Result`,
                    content,
                    type,
                  );
                }}
                className='flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground cursor-pointer w-fit mt-2'
              >
                <ArrowUpRight className='size-3' />
                View full
              </button>
            )}
          </div>
        );
      }

      case NodeDataType.CHAT_REQUEST:
        return <ChatRequestContent event={event} onOpenChat={onOpenChat} />;

      case NodeDataType.CHAT_RESPONSE:
      case NodeDataType.CHAT_EVENT:
        return <ChatMessageContent event={event} onOpenModal={onOpenModal} />;

      default:
        return null;
    }
  },
);

EventContent.displayName = 'EventContent';

export const EventsTimelineItem: React.FC<EventsTimelineItemProps> = React.memo(
  ({
    event,
    onOpenModal,
    onOpenChat,
    isConnected = false,
    isPaused = false,
    isEntering = false,
  }) => {
    const label = getEventLabel(event);
    const time = formatTime(event.timestamp);
    const showContent = isNodeEvent(event) && hasVisibleContent(event);

    if (
      isNodeEvent(event) &&
      event.display_type === NodeDataType.UPDATE &&
      event.event_type === NodeEventType.PROCESSING &&
      !event.dynamic_message
    ) {
      return null;
    }

    return (
      <EntryRow
        isEntering={isEntering}
        className='flex flex-col gap-2 px-4 py-2.5 min-w-0 overflow-hidden'
      >
        <div
          data-entry-label
          className='flex items-start justify-between gap-2 min-w-0'
        >
          <span className='text-sm text-foreground break-words min-w-0'>
            {label}
          </span>
          <span className='text-sm text-muted-foreground shrink-0 tabular-nums'>
            {time}
          </span>
        </div>
        {showContent && (
          // ``empty:hidden`` keeps the flex gap off rows whose content renders
          // nothing (e.g. a thinking event that arrived without text).
          <div data-entry-card className='min-w-0 empty:hidden'>
            <EventContent
              event={event as NodeEvent}
              onOpenModal={onOpenModal}
              onOpenChat={onOpenChat}
              isConnected={isConnected}
              isPaused={isPaused}
            />
          </div>
        )}
      </EntryRow>
    );
  },
);

EventsTimelineItem.displayName = 'EventsTimelineItem';
